import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {ProjectRelationService} from '../project-relation.service';
import {ProjectRelationRole} from '../../models/project-relation-role';
import {MockedProjectRelation, ProjectRelation, ProjectRelationVw} from '../../models/project-relation';
import {mockProjectReferences} from './project-reference.service.mock';
import {Company} from '../../models/company';
import {PageResponse, wrapInPageResponse} from '../../models/page-response';

export const mockRelations: MockedProjectRelation[] = [
    {
        id: 1,
        role: {
            key: 'Architect',
            value: 'Architect',
        },
        companyName: 'Recognize',
        street: 'Stationsstraat',
        houseNumber: '11',
        postalCode: '1234 AB',
        city: 'Almelo',
        phoneNumber: '+12345678910',
        contactPersonName: 'Romme Knol',
        contactPersonRole: 'Developer',
        reference: mockProjectReferences[0],
        readonly: false
    },
    {
        id: 2,
        role: {
            key: 'InvolvedVWCompany',
            value: 'InvolvedVWCompany',
        },
        companyName: 'Recognize',
        street: 'Stationsstraat',
        houseNumber: '11',
        postalCode: '1234 AB',
        city: 'Almelo',
        phoneNumber: '+12345678910',
        contactPersonName: 'Romme Knol',
        contactPersonRole: 'Developer',
        reference: mockProjectReferences[1],
        readonly: false
    },
    {
        id: 3,
        role: {
            key: 'Architect',
            value: 'Architect',
        },
        companyName: 'Recognize',
        street: 'Stationsstraat',
        houseNumber: '11',
        postalCode: '1234 AB',
        city: 'Almelo',
        phoneNumber: '+12345678910',
        contactPersonName: 'Romme Knol',
        contactPersonRole: 'Developer',
        reference: mockProjectReferences[2],
        readonly: false
    }
];

export const mockCompanies: Company[] = [
    {
        id: 1,
        companyName: 'Columbus B.V.',
        street: 'Isabellaan',
        houseNumber: '3',
        postalCode: '1492 ES',
        city: 'Palos',
        phoneNumber: '+12345678910'
    },
    {
        id: 2,
        companyName: 'Recognize',
        street: 'Stationsstraat',
        houseNumber: '11',
        postalCode: '7606GX',
        city: 'Almelo',
        phoneNumber: '06 12344213',
    },
    {
        id: 3,
        companyName: 'VWT',
        street: 'Ergens',
        houseNumber: '48',
        postalCode: '7606PP',
        city: null,
        phoneNumber: '06 01484777',
    }
];


@Injectable({
    providedIn: 'root'
})
export class ProjectRelationServiceMock implements ProjectRelationService {
    list(refId: number): Observable<ProjectRelation[]> {
        return of(mockRelations.filter(relation => relation.reference.id === refId));
    }

    createOther(refId: number, projectRelation: ProjectRelation): Observable<ProjectRelation> {
        const ref = mockProjectReferences.find(reference => reference.id === refId);
        if (ref) {
            mockRelations.push({
                ...projectRelation, reference: ref
            });
            return of(projectRelation);
        } else {
            throw Error('404 - reference not found');
        }

    }

    createVw(refId: number, createVwProjectRelation: ProjectRelationVw): Observable<ProjectRelation> {
        const ref = mockProjectReferences.find(reference => reference.id === refId);
        if (ref) {
            const mdmCompany = mockCompanies.find(company => company.id === createVwProjectRelation.mdmCompany);
            if (mdmCompany) {
                mockRelations.push({
                    role: {
                        key: 'InvolvedVWCompany',
                        value: 'Betrokken VolkerWessels onderneming'
                    },
                    ...mdmCompany,
                    id: mockRelations.length + 1,
                    contactPersonRole: 'Baas',
                    contactPersonName: 'Jan Jansen',
                    reference: ref,
                    readonly: false
                });
                return of(mockRelations[mockRelations.length - 1]);
            } else {
                throw Error('404 - company not found');
            }
        } else {
            throw Error('404 - reference not found');
        }
    }

    delete(refId: number, id: number): Observable<void> {
        const relationIdx = mockRelations.findIndex(relation => relation.id === id);
        if (relationIdx !== undefined) {
            mockRelations.splice(relationIdx, 1);
        }
        return of();
    }

    updateOther(refId: number, projectRelation: ProjectRelation): Observable<ProjectRelation> {
        const savedRelation = mockRelations.findIndex(relation => relation.id === projectRelation.id);
        if (savedRelation !== undefined) {
            mockRelations[savedRelation] = {...projectRelation, reference: mockRelations[savedRelation].reference};
        } else {
            throw Error('404 - relation not found');
        }
        return of(projectRelation);
    }

    updateVw(refId: number, projectRelation: ProjectRelationVw): Observable<ProjectRelation> {
        const savedRelation = mockRelations.findIndex(relation => relation.id === projectRelation.id);
        const company = mockCompanies.find(com => com.id === projectRelation.mdmCompany);
        if (savedRelation !== undefined && company !== undefined) {
            mockRelations[savedRelation] = {
                ...projectRelation,
                reference: mockRelations[savedRelation].reference,
                readonly: false,
                ...(company)
            };
            return of(mockRelations[savedRelation]);
        } else {
            throw Error('404 - relation not found');
        }
    }

    listMdmCompanies(search: string, page: number): Observable<PageResponse<Company>> {
        return of(wrapInPageResponse(mockCompanies));
    }

    listCustomCompanies(search: string, page: number): Observable<PageResponse<Company>> {
        return of(wrapInPageResponse(mockCompanies));
    }

    listRelationRoles(): Observable<ProjectRelationRole[]> {
        return of([
            {key: 'Architect', value: 'Architect'},
            {key: 'InvolvedVWCompany', value: 'Betrokken VolkerWessels onderneming'},
        ]);
    }

    customCompanyByCompanyName(companyName: string): Observable<Company> {
        const company = mockCompanies.find(com => com.companyName === companyName);
        return company ? of(company) : of();
    }
}
